/**
 * Two people handing things to each other, on screen.
 *
 * Two screens, the way the older game does it. On the first each side puts
 * things up and takes them back; on the second nothing can be changed and what
 * is left is reading the list and agreeing to it. Anything either of them
 * changes takes both agreements away, so the thing somebody agreed to is always
 * the thing in front of them.
 *
 * Nothing here decides anything. Every button sends what was pressed and the
 * window is redrawn from what the world says back, so a client that lies about
 * having agreed is a client that has told itself a story.
 */

import { INVENTORY_SIZE, type ItemStack } from '@inverse/common';
import type { ItemDefinitionWire, TradeStatePacket } from '@inverse/protocol';
import { TradeAct, type TradeActValue } from '@inverse/protocol';
import { initials, shortQuantity, tint } from './slots';
import { useDraggableWindow } from './draggable';

/**
 * One side's goods, drawn the same way a bag is.
 *
 * Always all twenty eight squares, filled or not. Nobody can offer more than a
 * bag holds, so the window can be the size of the most it will ever show, and
 * a window that stays still while things are put up is one where the goods stay
 * where they were put.
 */
function Offer({
  slots,
  describe,
  onSlot,
}: {
  slots: readonly (ItemStack | null)[];
  describe: (itemId: number) => ItemDefinitionWire | undefined;
  /** Pressing one, where that means anything. Left out for the other side's. */
  onSlot?: (slot: number) => void;
}): JSX.Element {
  return (
    <div className="trade-offer">
      {Array.from({ length: INVENTORY_SIZE }, (_, slot) => {
        const stack = slots[slot] ?? null;
        if (!stack) return <span className="slot" key={slot} />;

        const name = describe(stack.id)?.name ?? 'Something';
        return (
          <button
            type="button"
            className="slot filled"
            key={slot}
            disabled={!onSlot}
            data-name={name}
            title={name}
            onClick={() => onSlot?.(slot)}
          >
            <span className="slot-icon" style={{ background: tint(stack.id) }}>
              {initials(name)}
            </span>
            {describe(stack.id)?.stackable || stack.quantity > 1 ? (
              <span className="slot-quantity">{shortQuantity(stack.quantity)}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function TradeWindow({
  state,
  mine,
  theirs,
  describe,
  onAct,
}: {
  state: TradeStatePacket;
  mine: readonly (ItemStack | null)[];
  theirs: readonly (ItemStack | null)[];
  describe: (itemId: number) => ItemDefinitionWire | undefined;
  onAct: (act: TradeActValue, slot?: number) => void;
}): JSX.Element {
  // Moved, never resized: it is always exactly two bags wide.
  const { attach, style, onPointerDown } = useDraggableWindow('tradewindow');

  const confirming = state.stage >= 2;

  return (
    <div className="trade trading" ref={attach} style={style}>
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>{confirming ? 'Confirm the trade' : `Trading with ${state.name}`}</span>
      </div>

      <div className="panel-body trade-body">
        <div className="trade-side">
          <h4>You offer</h4>
          <Offer
            slots={mine}
            describe={describe}
            // Only while things can still be moved. On the confirming screen a
            // press that quietly took something back would be the one way to
            // hand over less than was agreed.
            onSlot={confirming ? undefined : (slot) => onAct(TradeAct.Withdraw, slot)}
          />
        </div>

        <div className="trade-divider" aria-hidden="true" />

        <div className="trade-side">
          <h4>
            {state.name} offers
            {state.theyAgreed ? <span className="trade-agreed">agreed</span> : null}
          </h4>
          <Offer slots={theirs} describe={describe} />
        </div>
      </div>

      {/* What each screen is for, said once. Somebody handing over their goods
          should not have to work out which half of this they are looking at. */}
      <p className="hint trade-said">
        {confirming
          ? 'Nothing can be changed now. Look at both lists, then accept.'
          : 'Press something in your bag to put it up, or in your offer to take it back.'}
      </p>

      <div className="trade-buttons">
        <button
          type="button"
          className={state.agreed ? 'trade-accept on' : 'trade-accept'}
          onClick={() => onAct(TradeAct.Agree)}
        >
          {state.agreed ? 'Waiting' : confirming ? 'Accept' : 'Accept'}
        </button>
        <button type="button" onClick={() => onAct(TradeAct.Close)}>
          Decline
        </button>
      </div>
    </div>
  );
}
